import { useQuery } from "@apollo/client";
import Box from "@mui/material/Box";
import React, { useMemo, useRef, useState } from "react";
import SimpleColumnChart from "../../../../../../components/charts/SimpleColumnChart/SimpleColumnChart";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import { useGetMarketSize } from "../../../../hooks/useGetMarketSize";
import {
  COMPETITOR_PRESENCE_BY_DEPARTMENT_QUERY,
  CompetitorPresenceByDepartmentPayload,
} from "../../../../../../apis/companySearch/presenceByDepartment";
import { BarChartColors } from "../../../../../../components/charts/constants";
import { useWorkspaceStore } from "../../../../../../store/workspace/WorkspaceStore";
import {
  determineChartContent,
  formatPenetrationPercentage,
  hasNoData,
  safeCalculatePercentage,
  isValidChartValue,
} from "../../../../utils";
import { useCombinedFilters } from "../../../../../../hooks/useCombinedFilters";
import * as am4charts from "@amcharts/amcharts4/charts";
import { ChartExportMenu } from "../../../../../../components/charts/ChartExportMenu";
import { Market } from "../../../../../../types/workspace";
import { TitleWithTooltipInfo } from "../../../../../../components/charts/TitleWithTooltipInfo";
import { CHART_CONSTANTS } from "../../../constants";

const { chartTitle, tooltip, baseId, csvFieldMappings } =
  CHART_CONSTANTS.competitorPresenceByDepartment;

export const CompetitorPresenceByDepartmentChart: React.FC = () => {
  const [isChartHovered, setIsChartHovered] = useState(false);
  const selectedMarket = useWorkspaceStore((state) => state.selectedMarket);
  const chartRef = useRef<am4charts.XYChart>(null);
  const combinedFilters = useCombinedFilters();
  const competitorsProductIds = useWorkspaceStore(
    (state) => state.draft?.configuration.competitorsProductIds || []
  );
  const marketSize = useGetMarketSize();
  const market = marketSize[selectedMarket || Market.TAM];
  const localizeInstalls = useWorkspaceStore(
    (state) => state.draft?.configuration?.localizeInstalls ?? true
  );
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const { data, loading } = useQuery<CompetitorPresenceByDepartmentPayload>(
    COMPETITOR_PRESENCE_BY_DEPARTMENT_QUERY,
    {
      variables: {
        companyFilters: combinedFilters,
        productIds: competitorsProductIds,
        localizeInstalls,
        companyMatchFile:
          combinedFilters?.opportunityType &&
          workspaceWhitespace?.fileId &&
          workspaceWhitespace?.lastMatched
            ? workspaceWhitespace
            : undefined,
      },
      skip: market.loading,
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
    }
  );

  const isLoadingData = useMemo(() => {
    return loading || market.loading;
  }, [loading, market.loading]);

  const chartData = useMemo(() => {
    if (!market.companyCount) return [];

    return (
      data?.presenceByDepartment
        ?.filter(
          (item) =>
            item.installsCount > 0 && Number.isFinite(item.installsCount)
        )
        .map((item) => {
          const penetrationPercentage = safeCalculatePercentage(
            item.installsCount,
            market.companyCount
          );
          return {
            ...item,
            marketPercentage: penetrationPercentage,
            formattedPercentage: formatPenetrationPercentage(
              penetrationPercentage
            ),
          };
        })
        .filter((item) => isValidChartValue(item.marketPercentage))
        .sort((a, b) => b.installsCount - a.installsCount)
        .slice(0, 10) || []
    );
  }, [data?.presenceByDepartment, market.companyCount]);

  const noDataReturned = useMemo(
    () => hasNoData(isLoadingData, chartData),
    [chartData, isLoadingData]
  );
  const shouldShowExport = chartRef && !noDataReturned && !isLoadingData;

  const chartContent = useMemo(() => {
    return determineChartContent({
      productIds: competitorsProductIds,
      isLoading: isLoadingData,
      noDataReturned,
      baseId,
      chartToRender: (
        <Box height={370}>
          <SimpleColumnChart
            data={chartData}
            id={`${baseId}-chart`}
            xAxisDataPoint="faiDepartmentName"
            yAxisDataPoint="installsCount"
            yAxisLabel="# of Companies"
            xAxisLabel="Department"
            columnTooltipFormat={
              "[bold]{faiDepartmentName}: {installsCount}\nMarket Share: {formattedPercentage}%"
            }
            truncateXAxisLabels
            xAxisTooltipFormat="{faiDepartmentName}"
            barColor={BarChartColors}
            ref={chartRef}
          />
        </Box>
      ),
    });
  }, [chartData, isLoadingData, noDataReturned, competitorsProductIds]);

  return (
    <Box
      id={`${baseId}-chart-wrapper`}
      display="flex"
      flexDirection="column"
      gap="31px"
      border="solid 1px #edeff1"
      borderRadius="10px"
      padding="20px 20px 13px"
      minHeight={300}
      bgcolor={
        noDataReturned || competitorsProductIds.length === 0
          ? "#edeff1"
          : "#fff"
      }
      onMouseEnter={() => setIsChartHovered(true)}
      onMouseLeave={() => setIsChartHovered(false)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <TitleWithTooltipInfo
          tooltipTitle={tooltip}
          chartTitle={chartTitle}
          showInfoIcon={isChartHovered}
        />
        {shouldShowExport && (
          <ChartExportMenu
            chart={chartRef}
            title={chartTitle}
            csvFieldMappings={csvFieldMappings}
          />
        )}
      </Box>
      {chartContent}
    </Box>
  );
};
